import type { ApiResponse, Rankings, TournamentDetail } from '@drawgon/shared';

/**
 * 公開サイトの API クライアント。
 *
 * **Hono RPC は使わない**（[ADR-007]）。このサイトが叩くのはランキング程度で、
 * `@drawgon/api` にビルド依存する価値が無いため。型は `@drawgon/shared` から取る。
 *
 * [ADR-007]: docs/adr/007-hono-rpc-web-only.md
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly code?: string
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(path: string): Promise<T> {
  let res: Response;

  try {
    res = await fetch(`${BASE_URL}/api/v1${path}`);
  } catch {
    // ネットワーク到達不可。公開サイトなので、利用者に技術的な詳細は見せない。
    throw new ApiRequestError('データを取得できませんでした');
  }

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    // API が落ちていると proxy が空の応答を返す。既定の
    // "Unexpected end of JSON input" は何が起きたか語らないので変換する。
    throw new ApiRequestError(`データを取得できませんでした（HTTP ${res.status}）`);
  }

  if (!body.success || body.data === undefined) {
    throw new ApiRequestError(
      body.error?.message ?? 'データを取得できませんでした',
      body.error?.code
    );
  }

  return body.data;
}

/**
 * ランキング（#284）。**シーズンは日本時間の暦の 1 か月**で、`season`（`YYYY-MM`）が無ければ今のシーズン。
 * 区切りはサーバーが決めて `season` で返す（ブラウザの時計や時間帯に左右されない）。
 */
export function fetchRankings(season?: string): Promise<Rankings> {
  const query = season ? `?season=${encodeURIComponent(season)}` : '';
  return request<Rankings>(`/rankings${query}`);
}

/**
 * 大会の詳細（ブラケット）。**認証が要らない**（`docs/specs/api.md`）。
 *
 * 通知からアプリに戻れない人（アプリを消した・別の端末）の受け皿になるページなので、
 * **ログインを求めない**（企画書 §17.2 / §37-4）。
 */
export function fetchTournament(id: string): Promise<TournamentDetail> {
  return request<TournamentDetail>(`/tournaments/${encodeURIComponent(id)}`);
}
